import { baseThemeValues } from 'constants/common';

const baseDatePresetTooltipButton = {
  borderRadius: '6px',
  borderWidth: '1px',
  fontFamily: baseThemeValues.inherit,
  fontSize: '14px',
  fontWeight: '400',
};

const baseButtonProps = {
  borderRadius: '6px',
  boxShadow: 'palette.transparent',
  fontFamily: baseThemeValues.inherit,
  fontSize: '14px',
  fontWeight: 600,
  outlineColor: 'palette.transparent',
  outlineWidth: '0px',
};

const baseOutlinedProps = {
  ...baseButtonProps,
  borderWidth: '1px',
};

const basePrimaryProps = {
  ...baseButtonProps,
  borderWidth: '0px',
};

const dayBase = {
  borderRadius: '40px',
  fontFamily: baseThemeValues.inherit,
  fontSize: '14px',
};

const presetButtonBase = {
  borderRadius: '56px',
  borderWidth: '1px',
  fontFamily: baseThemeValues.inherit,
  fontSize: '14px',
  fontWeight: '600',
};

const presetButton = {
  base: {
    ...presetButtonBase,
    bg: 'palette.transparent',
    borderColor: 'palette.neutral.40',
    text: 'palette.neutral.80',
  },
  hover: {
    ...presetButtonBase,
    bg: 'palette.transparent',
    borderColor: 'palette.neutral.80',
    text: 'palette.neutral.90',
  },
  pressed: {
    ...presetButtonBase,
    bg: 'palette.surface.4',
    borderColor: 'palette.neutral.95',
    text: 'palette.neutral.95',
  },
  selected: {
    ...presetButtonBase,
    bg: 'palette.primary.20',
    borderColor: 'palette.primary.20',
    text: 'palette.primary.99',
  },
};

const nextArrowButton = {
  active: {
    bg: 'palette.primary.20',
    fontFamily: baseThemeValues.inherit,
    fontSize: '12px',
    fontWeight: 400,
    text: 'palette.primary.99',
  },
  base: {
    bg: 'palette.surface.5',
    fontFamily: baseThemeValues.inherit,
    fontSize: '12px',
    fontWeight: 400,
    text: 'palette.tertiary.80',
  },
  hover: {
    bg: 'palette.surface.2',
    fontFamily: baseThemeValues.inherit,
    fontSize: '12px',
    fontWeight: 400,
    text: 'palette.tertiary.95',
  },
  pressed: {
    bg: 'palette.surface.3',
    fontFamily: baseThemeValues.inherit,
    fontSize: '12px',
    fontWeight: 400,
    text: 'palette.tertiary.80',
  },
};

export const datepickerTheme = {
  calendarInnerMenu: {
    button: presetButton,
  },
  calendarMenuButton: {
    active: {
      bg: 'palette.primary.20',
      borderRadius: '4px',
      icon: 'palette.primary.99',
    },
    base: {
      bg: 'palette.transparent',
      borderRadius: '4px',
      icon: 'palette.tertiary.50',
    },
    hover: {
      bg: 'palette.tertiary.10',
      borderRadius: '4px',
      icon: 'palette.tertiary.95',
    },
    pressed: {
      bg: 'palette.tertiary.20',
      borderRadius: '4px',
      icon: 'palette.tertiary.95',
    },
  },
  container: {
    bg: 'palette.surface.2',
    borderRadius: '12px',
    boxShadowColor: 'palette.black:0.5',
  },
  controlButtons: {
    applyButton: {
      base: {
        ...basePrimaryProps,
        bg: 'palette.primary.20',
        text: 'palette.primary.99',
      },
      disabled: {
        ...basePrimaryProps,
        bg: 'palette.tertiary.20',
        text: 'palette.tertiary.40',
      },
      hover: {
        ...basePrimaryProps,
        bg: 'palette.primary.10',
        text: 'palette.primary.99',
      },
      pressed: {
        ...basePrimaryProps,
        bg: 'palette.primary.20',
        text: 'palette.primary.99',
      },
    },
    cancelButton: {
      base: {
        ...baseOutlinedProps,
        bg: 'palette.transparent',
        borderColor: 'palette.neutral.50',
        text: 'palette.neutral.50',
      },
      disabled: {
        ...baseOutlinedProps,
        bg: 'palette.transparent',
        borderColor: 'palette.tertiary.20',
        text: 'palette.tertiary.20',
      },
      hover: {
        ...baseOutlinedProps,
        bg: 'palette.transparent',
        borderColor: 'palette.neutral.90',
        text: 'palette.neutral.90',
      },
      pressed: {
        ...baseOutlinedProps,
        bg: 'palette.surface.5',
        borderColor: 'palette.neutral.50',
        text: 'palette.neutral.90',
      },
    },
  },
  datePreset: {
    button: presetButton,
    container: {
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.10',
    },
    input: {
      base: {
        bg: 'palette.surface.3',
        borderColor: 'palette.tertiary.10',
        fontFamily: baseThemeValues.inherit,
        fontSize: '14px',
        fontWeight: '400',
        placeholder: 'palette.tertiary.40',
        text: 'palette.tertiary.90',
      },
      hover: {
        bg: 'palette.surface.5',
        borderColor: 'palette.tertiary.20',
        fontFamily: baseThemeValues.inherit,
        fontSize: '14px',
        fontWeight: '400',
        placeholder: 'palette.tertiary.40',
        text: 'palette.tertiary.90',
      },
    },
  },
  datePresetTooltipButton: {
    base: {
      ...baseDatePresetTooltipButton,
      bg: 'palette.surface.4',
      borderColor: 'palette.tertiary.10',
      text: 'palette.tertiary.50',
    },
    hover: {
      ...baseDatePresetTooltipButton,
      bg: 'palette.surface.4',
      borderColor: 'palette.neutral.90',
      text: 'palette.neutral.90',
    },
    pressed: {
      ...baseDatePresetTooltipButton,
      bg: 'palette.surface.5',
      borderColor: 'palette.neutral.50',
      text: 'palette.neutral.90',
    },
  },
  day: {
    base: {
      ...dayBase,
      bg: 'palette.transparent',
      fontWeight: 400,
      text: 'palette.neutral.80',
    },
    current: {
      ...dayBase,
      bg: 'palette.primary.70',
      fontWeight: 400,
      text: 'palette.tertiary.95',
    },
    disabled: {
      ...dayBase,
      bg: 'palette.transparent',
      fontWeight: 400,
      text: 'palette.neutral.40',
    },
    hover: {
      ...dayBase,
      bg: 'palette.tertiary.30',
      fontWeight: 500,
      text: 'palette.tertiary.95',
    },
    isRangeSelected: {
      ...dayBase,
      bg: 'palette.tertiary.10',
      fontWeight: 500,
      text: 'palette.tertiary.95',
    },
    isSelected: {
      ...dayBase,
      bg: 'palette.primary.20',
      fontWeight: 500,
      text: 'palette.primary.99',
    },
  },
  dayBg: {
    hover: {
      bg: 'palette.tertiary.40',
      borderColor: 'palette.tertiary.30',
    },
    isSelected: {
      bg: 'palette.tertiary.10',
      borderColor: 'palette.transparent',
    },
  },
  manualInputs: {
    button: 'palette.tertiary.95',
    label: {
      fontFamily: baseThemeValues.inherit,
      fontSize: '16px',
      fontWeight: 400,
      text: 'palette.tertiary.95',
    },
  },
  maskedTimeInput: {
    activeIcon: 'palette.primary.20',
  },
  monthLabel: {
    fontFamily: baseThemeValues.inherit,
    fontSize: '16px',
    fontWeight: 500,
    text: 'palette.tertiary.95',
  },
  suggestions: {
    dividerLine: 'palette.tertiary.20',
    suggestion: {
      hover: {
        bg: 'palette.surface.4',
      },
      inactive: {
        bg: 'palette.transparent',
        date: 'palette.tertiary.70',
        name: 'palette.tertiary.70',
      },
      selected: {
        date: 'palette.primary.99',
        icon: 'palette.primary.99',
        name: 'palette.primary.99',
      },
    },
    title: {
      fontFamily: baseThemeValues.inherit,
      fontSize: '16px',
      fontWeight: 500,
      text: 'palette.primary.99',
    },
  },
  timeGroup: {
    bg: 'palette.tertiary.20',
    button: nextArrowButton,
  },
  timePickerElement: {
    label: 'palette.tertiary.50',
  },
  tooltipMonthYear: {
    bg: 'palette.surface.5',
    borderRadius: '6px',
    tooltipLabel: {
      fontFamily: baseThemeValues.inherit,
      fontSize: '16px',
      fontWeight: 500,
      text: 'palette.tertiary.95',
    },
  },
  weekDay: {
    fontFamily: baseThemeValues.inherit,
    fontSize: '14px',
    fontWeight: 500,
    text: 'palette.neutral.80',
  },
};
